import { useCallback, useState } from 'react';

import * as http from 'utils/http';
import config from 'config';
import debouncePromise from 'debounce-promise';
import { useLocation } from 'react-router-dom';

const TYPE = {
  J: 'J',
  S: 'S',
};

const getIdType = (id) => {
  if (!id) {
    return null;
  }
  const firstChar = id[0];
  if (!firstChar || !isNaN(firstChar)) {
    return null;
  }
  if (firstChar.toUpperCase() === TYPE.J) {
    return TYPE.J;
  }
  if (firstChar.toUpperCase() === TYPE.S) {
    return TYPE.S;
  }
};

const findChildSO = (childs, value) => childs?.find(({ id }) => id === getId(value));
const countChildSOs = (childs) => childs?.length;

const getId = (fullId) => (!!getIdType(fullId) ? Number(fullId.substring(1)) : Number(fullId));
const getFullId = ({ id, type }) => `${type}${id}`;

const isValidValueForRequest = (value) =>
  !!value && (value.length > 1 || (value.length === 1 && !isNaN(parseInt(value, 10))));

const request = debouncePromise(async (value) => {
  const validForRequest = isValidValueForRequest(value);
  if (validForRequest) {
    const tree = await http.get(`${config.API_URL}search/?term=${value}`);
    return Promise.resolve(tree);
  }

  return Promise.resolve([]);
}, 600);

const ARROW_UP = 38;
const ARROW_DOWN = 40;
const ENTER_KEY = 13;

const getTabToOpenSearch = (path) => {
  if (!path) return null;
  if (path.includes('tasks')) {
    return '/tasks';
  }

  return null;
};

const generateOptionsFromTree = (tree, value, simplified) => {
  tree = tree.sort((a, b) => {
    const timeA = new Date(a.start_time_from || undefined);
    const timeB = new Date(b.start_time_from || undefined);
    const isAValid = !isNaN(timeA.getDate());
    const isBValid = !isNaN(timeB.getDate());

    if (isAValid && isBValid) {
      return timeB - timeA;
    } else if (isAValid) {
      return -1;
    }
    return 1;
  });

  if (simplified) {
    return tree.reduce((current, item) => {
      const countSOs = countChildSOs(item.childs);
      const itemId = getFullId(item);
      let entry = {
        soBolded: false,
      };

      if (item.type === TYPE.J) {
        entry = {
          ...entry,
          jobId: itemId,
          soId: countSOs === 1 ? getFullId(item.childs[0]) : null,
          soLabel: countSOs > 1 ? ` ${countSOs} SOs` : null,
        };
      } else {
        entry = {
          ...entry,
          jobId: null,
          soId: itemId,
          soLabel: null,
        };
      }

      return [...current, entry];
    }, []);
  }

  return tree.reduce((current, item) => {
    const countSOs = countChildSOs(item.childs);
    const jobId = item.type === TYPE.J ? getFullId(item) : null;
    const searchType = getIdType(value) || (getId(jobId) === Number(value) ? TYPE.J : TYPE.S);

    if (searchType === TYPE.J) {
      return [
        ...current,
        {
          jobId: getFullId(item),
          soBolded: false,
          soId: countSOs === 1 ? getFullId(item.childs[0]) : null,
          soLabel: countSOs > 1 ? ` ${countSOs} SOs` : null,
        },
      ];
    }
    if (searchType === TYPE.S) {
      const soId = item.type === TYPE.S ? getFullId(item) : null;
      const childSo = findChildSO(item.childs, value);
      return [
        ...current,
        {
          jobId,
          soBolded: true,
          soId: soId || getFullId(childSo),
          soLabel: countSOs > 1 ? ` + ${countSOs - 1} SOs` : null,
        },
      ];
    }
    return current;
  }, []);
};

const useSearcher = ({ openShipperOrderDetail, openJourneyDetail, simplified }) => {
  const [value, setValue] = useState('');
  const [opened, setOpened] = useState(false);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(false);
  const [closeOnBlur, setCloseOnBlur] = useState(true);
  const location = useLocation();

  const handleOpen = useCallback(
    (value) => {
      if (!value) {
        return;
      }

      setValue('');
      setOpened(false);
      setLoading(false);
      setSelectedIndex(-1);

      if (value[0] === TYPE.S) {
        openShipperOrderDetail(parseInt(value.substring(1), 10));
        return;
      }

      if (value[0] === TYPE.J) {
        const tab = getTabToOpenSearch(location?.pathname);
        openJourneyDetail({
          id: parseInt(value.substring(1), 10),
          ...(tab && { tab }),
        });
        return;
      }
    },
    [openShipperOrderDetail, openJourneyDetail, location, setSelectedIndex]
  );

  const handleBlur = useCallback(() => {
    if (closeOnBlur) {
      setOpened(false);
    }
    setCloseOnBlur(true);
    setFocused(false);
  }, [closeOnBlur]);

  const handleFocus = useCallback(
    (value) => {
      if (isValidValueForRequest(value) && options && options.length) {
        setOpened(true);
      }
      setFocused(true);
    },
    [options]
  );

  const handleClear = useCallback(() => {
    setValue('');
    setError(false);
    setOpened(false);
  }, [setValue, setError, setOpened]);

  const handleClick = useCallback(
    (selectedValue) => {
      handleOpen(selectedValue);
    },
    [handleOpen]
  );

  const handleMouseDown = useCallback(() => {
    setCloseOnBlur(false);
  }, []);

  const handleKeyUp = useCallback(
    (e) => {
      const keyCode = e.which || e.keyCode;
      if (keyCode === ENTER_KEY && selectedIndex >= 0) {
        const option = options[selectedIndex];
        handleOpen(option.jobId || option.soId);
      }

      if (keyCode === ARROW_DOWN && selectedIndex < options.length - 1) {
        setSelectedIndex(selectedIndex + 1);
      }
      if (keyCode === ARROW_UP && selectedIndex > 0) {
        setSelectedIndex(selectedIndex - 1);
      }
    },
    [handleOpen, selectedIndex, options]
  );

  const handleChange = useCallback(
    async (_, { value }) => {
      setValue(value.toUpperCase());
      setError(false);
      if (isValidValueForRequest(value)) {
        setOpened(true);
        setLoading(true);
        setSelectedIndex(-1);
      } else {
        setLoading(false);
      }
      try {
        const tree = await request(value);
        if (tree.length) {
          setOptions(generateOptionsFromTree(tree, value, simplified));
        } else {
          if (isValidValueForRequest(value)) {
            setError(true);
            setOpened(false);
            setOptions([]);
          }
        }
      } catch (e) {
        setError(true);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [simplified]
  );

  return {
    opened,
    focused,
    loading,
    error,
    handleClick,
    handleMouseDown,
    handleBlur,
    handleFocus,
    handleClear,
    handleKeyUp,
    handleChange,
    value,
    selectedIndex,
    options,
  };
};

export { useSearcher };
